import { CustomButton } from "@/components/custom/custom-button";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { FormSelectInput } from "@/components/custom/custom-form-select-input";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { showToast } from "@/redux/actions/toast-actions";
import { newAdminSchema } from "@/schemas/user.schema";
import { formatPhoneNumber } from "@/utils/formatPhone";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  styled,
  Stack,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    maxWidth: 500,
    width: "100%",
  },
}));

const transformPayload = (values) => ({
  firstName: values.firstName,
  surname: values.surname,
  email: values.email,
  phoneNumber: formatPhoneNumber(values.phoneNumber),
  password: values.password,
  roleName: values.roleName,
  isAdmin: true,
  accessType: values.accessType,
});

const NewAdminDialog = ({ open, onClose, roleOptions, mutationFn }) => {
  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const formik = useFormik({
    initialValues: {
      firstName: "",
      surname: "",
      email: "",
      phoneNumber: "",
      password: "",
      roleName: "",
      accessType: "",
    },
    validationSchema: newAdminSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      mutation.mutate(transformPayload(values));
    },
  });

  const mutation = useMutation({
    mutationFn: (payload) => mutationFn(payload),
    onSuccess: (data) => {
      formik.resetForm();
      dispatch(showToast(data?.message ?? "Login successful", "success"));
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["adminStats"] });
      onClose();
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    formik.resetForm();
    mutation.reset();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          px: 2,
          pt: 2,
          pb: 0,
        }}
      >
        <Box>
          <DialogTitle
            sx={{
              p: 0,
              fontSize: "1.1rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            Create New Admin
          </DialogTitle>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Fill in the details below to create a new admin user.
          </Typography>
        </Box>

        <Stack spacing={2}>
          <FormTextInput formik={formik} name="firstName" label="First Name" />
          <FormTextInput formik={formik} name="surname" label="Last Name" />
          <FormTextInput
            formik={formik}
            name="email"
            label="Email"
            type="email"
          />
          <FormTextInput
            formik={formik}
            name="phoneNumber"
            label="Phone Number"
          />
          <FormTextInput
            formik={formik}
            name="password"
            label="Password"
            type="password"
          />

          <FormSelectInput
            options={roleOptions}
            formik={formik}
            name="roleName"
            label="Role"
          />
          <FormSelectInput
            options={[
              { label: "Role Initiator", value: "ROLE_INITIATOR" },
              { label: "Role Approval", value: "ROLE_APPROVAL" },
            ]}
            formik={formik}
            name="accessType"
            label="Access Type"
          />
        </Stack>

        {mutation.isError && (
          <CustomErrorMessage
            message={
              mutation.error?.message ?? "Failed to create admin. Try again."
            }
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 2, pb: 1, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={mutation.isPending}
          sx={{ px: 2 }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          size="small"
          disabled={!formik.isValid || !formik.dirty || mutation.isPending}
          isLoading={mutation.isPending}
          onClick={formik.handleSubmit}
          sx={{ px: 2 }}
        >
          {mutation.isPending ? "Creating..." : "Create Admin"}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default NewAdminDialog;
